// Angular
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
// Models
import { ControlsOf } from '@shared/models-v3/controls-of.type';
// PrimeNG
import { InputTextModule } from 'primeng/inputtext';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [InputTextModule];

@Component({
  selector: 'app-name-input',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES],
  templateUrl: './name-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NameInputComponent),
      multi: true,
    },
  ],
  host: { class: 'w-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NameInputComponent
  implements ControlValueAccessor, Validator, OnInit
{
  form!: FormGroup<ControlsOf<{ name: string }>>;
  private onChange: (value: string) => void = () => {};
  private onTouch: () => void = () => {};

  //#region Getters
  get nameField(): FormControl<string> {
    return this.form.controls.name;
  }
  //#endregion

  //#region Dependencies
  private readonly fb = inject(NonNullableFormBuilder);
  //#endregion

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm(): void {
    this.form = this.fb.group({
      name: [''],
    });
  }

  /**
   * Maneja el evento de entrada del campo de texto y formatea el valor ingresado.
   * @param event - Evento del DOM que contiene la referencia al elemento input
   * @remarks
   * Este método se ejecuta en cada cambio del valor del input.
   * Filtra los caracteres no permitidos y actualiza el valor del FormControl
   * usando el método getValidCharacters.
   * @example
   * // Al escribir "Juan123" en el input
   * onTextFieldInput(event) // actualiza el campo a "Juan"
   */
  onTextFieldInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const formattedValue = this.getValidCharacters(inputElement.value);
    this.nameField.setValue(formattedValue);
    this.onChange(formattedValue);
  }

  /**
   * Maneja el evento 'blur' del campo de texto.
   * Elimina los espacios en blanco al inicio y final del valor ingresado.
   * @param event - Evento del DOM que contiene la referencia al elemento input
   * @remarks
   * Este método se ejecuta cuando el campo de texto pierde el foco.
   * Actualiza el valor del FormControl eliminando espacios innecesarios.
   */
  onTextFieldBlur(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const formattedValue = inputElement.value.trim();
    this.nameField.setValue(formattedValue);
    this.onTouch();
  }

  /**
   * Limpia y formatea una cadena de texto para que solo contenga caracteres válidos para nombres.
   * @param value - Cadena de texto a validar y formatear
   * @returns Cadena limpia que solo contiene letras (incluyendo acentos y ñ) y espacios.
   * Los espacios múltiples se reducen a uno solo.
   * @example
   * getValidCharacters('Juan  123  Pérez') // returns 'Juan Pérez'
   * getValidCharacters('') // returns ''
   */
  private getValidCharacters(value: string): string {
    if (!value) return '';
    return value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, ' ');
  }

  //#region ControlValueAccessor
  /**
   * Implementación del método writeValue de ControlValueAccessor.
   * Establece el valor del campo de nombre en el formulario.
   * @param value - El valor a establecer en el campo de nombre
   * @remarks
   * Este método es llamado por Angular cuando el valor del control debe actualizarse
   * programáticamente, por ejemplo cuando se usa [(ngModel)] o formControl.
   * @example
   * // Cuando el valor del control cambia externamente
   * component.writeValue('Juan Pérez'); // actualiza el campo a "Juan Pérez"
   */
  writeValue(value: string): void {
    this.nameField.setValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(disabled: boolean): void {
    disabled ? this.nameField.disable() : this.nameField.enable();
  }
  //#endregion

  //#region Validator
  /**
   * Validar el control síncronamente.
   * @returns The validation errors or null.
   * */
  validate(): ValidationErrors | null {
    return this.nameField.errors;
  }
  //#endregion
}
